import { Fragment, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  AppWindow,
  ArchiveRestore,
  ChevronsUpDown,
  BotMessageSquare,
  Boxes,
  Columns2,
  FolderGit2,
  History,
  Gauge,
  LayoutTemplate,
  Pencil,
  Save,
  MessageCircleQuestion,
  PanelBottom,
  PanelLeft,
  PanelRight,
  PanelTop,
  Plus,
  RotateCcw,
  ScrollText,
  Settings,
  SquareKanban,
  Trash2,
  User,
  X,
  EyeOff,
  Eye,
  Check,
  CopyPlus,
  Settings2,
  SquareTerminal,
} from "lucide-react";
import type { SidebarView } from "@/components/board-sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Hint } from "@/components/ui/tooltip";
import { WINDOW_SHORTCUTS } from "@/components/workspace";
import { useLayoutActions, useLayouts } from "@/hooks/use-layouts";
import { useCan } from "@/lib/auth";
import {
  applyLayout,
  clearShelf,
  activateTab,
  closeTab,
  closeWindow,
  viewKey,
  cycleLayoutId,
  focusedWindow,
  layoutFromWorkspace,
  restoreAll,
  reveal,
  setActiveLayout,
  openEmptyWindow,
  openView,
  presetSideBySide,
  restoreWindow,
  setNav,
  unmaximize,
  windows,
  type Dock,
  type Workspace,
} from "@/lib/workspace";
import type { AgentWithContext, BoardDetail, SavedLayoutNode, WorkspaceLayout } from "@/lib/types";
import { cn } from "@/lib/utils";

/** The icon a view is drawn with: in a window's title bar and in this menu. */
export function viewIcon(view: SidebarView): ReactNode {
  switch (view.kind) {
    case "board":
    case "boards":
      return <SquareKanban />;
    case "agents":
      return <BotMessageSquare />;
    case "queue":
      return <User />;
    case "waiting":
      return <MessageCircleQuestion />;
    case "status":
      return <Gauge />;
    case "apps":
      return <Boxes />;
    case "activity":
      return <ScrollText />;
    case "projects":
      return <FolderGit2 />;
    case "changelog":
      return <History />;
    case "terminal":
      return <SquareTerminal />;
    case "empty":
      return <AppWindow />;
    default:
      return <Settings />;
  }
}

const DOCKS: Array<[Dock, string, ReactNode]> = [
  ["left", "Left", <PanelLeft key="l" />],
  ["right", "Right", <PanelRight key="r" />],
  ["top", "Top", <PanelTop key="t" />],
  ["bottom", "Bottom", <PanelBottom key="b" />],
];

/**
 * Whether a saved layout's board or agent still exists. Unknown while a list has not
 * loaded counts as existing: better a view that says "not found" for a moment
 * than a layout whose every window turned into Status.
 */
export function layoutViewExists(view: SidebarView, allBoards: BoardDetail[], agents: Array<{ id: string }>): boolean {
  if (view.kind === "board") return allBoards.length === 0 || allBoards.some((detail) => detail.board.id === view.boardId);
  if (view.kind === "agents" && view.agentId) return agents.length === 0 || agents.some((agent) => agent.id === view.agentId);
  return true;
}

/**
 * Every window, open and closed, in one menu: focus or close what is open, bring
 * back what was closed (to where it was, here, or beside), open any view in a new
 * window, apply a layout, and place the navigation. It is the undo for window
 * operations, which deliberately are not history entries.
 */
export function WindowsMenu({
  ws,
  apply,
  titleOf,
  boards,
  allBoards = boards,
  agents,
  canAwait,
  children,
  side = "right",
  align = "start",
  open: controlledOpen,
  onOpenChange,
  openLayouts = false,
}: {
  ws: Workspace;
  apply: (op: (ws: Workspace) => Workspace) => void;
  titleOf: (view: SidebarView) => string;
  boards: BoardDetail[];
  /** Archived boards too: a saved layout may open one, as the archive does. */
  allBoards?: BoardDetail[];
  agents: AgentWithContext[];
  /** The Awaiting view exists for this person (admins). */
  canAwait: boolean;
  /** The trigger, rendered as the menu's `asChild`. */
  children: ReactNode;
  side?: "right" | "left" | "top" | "bottom";
  align?: "start" | "end";
  /** Open state held by the caller, for a menu opened from another menu's item; otherwise the trigger drives it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Opens with the Layouts submenu already out (the Layouts menu's Manage…). */
  openLayouts?: boolean;
}) {
  const open = windows(ws);
  const focused = focusedWindow(ws);
  // Controlled, so Rename (an inline button, which does not select its row) can close the menu before its dialog opens.
  const [ownOpen, setOwnOpen] = useState(false);
  const menuOpen = controlledOpen ?? ownOpen;
  const setMenuOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };
  const { layouts, layoutActions, canSave, active, applySaved, overwrite, askName, onCloseAutoFocus, nameDialog } = useLayoutCommands({
    ws,
    apply,
    allBoards,
    agents,
    closeMenu: () => setMenuOpen(false),
  });
  // Manage… in the Layouts menu opens this one with its Layouts submenu out.
  // A tick after the menu opens: set at once, the menu's own focus on opening closed it again.
  const [layoutsSub, setLayoutsSub] = useState(false);
  useEffect(() => {
    if (!menuOpen) setLayoutsSub(false);
    if (!menuOpen || !openLayouts) return;
    const timer = window.setTimeout(() => setLayoutsSub(true), 60);
    return () => window.clearTimeout(timer);
  }, [menuOpen, openLayouts]);
  const beside = (view: SidebarView) =>
    apply((current) => openView(current, view, { kind: "split", targetId: current.focusedId, zone: "right" }, { duplicate: true }));
  const chats = agents.filter((agent) => agent.kind !== "master");
  const isOwner = useCan("owner");
  const firstBoard = (focused.view.kind === "board" ? focused.view : null) ?? (boards[0] ? { kind: "board" as const, boardId: boards[0].board.id } : null);
  const openAgent = open.find((window) => window.view.kind === "agents" && window.view.agentId)?.view;
  const firstAgent = openAgent ?? (chats[0] ? { kind: "agents" as const, agentId: chats[0].id } : { kind: "agents" as const });
  const views: Array<[SidebarView, string]> = [
    [{ kind: "queue" }, "My tasks"],
    ...(canAwait ? ([[{ kind: "waiting" }, "Awaiting your answer"]] as Array<[SidebarView, string]>) : []),
    [{ kind: "status" }, "Status"],
    [{ kind: "apps" }, "Apps"],
    ...(isOwner ? ([[{ kind: "terminal" }, "Terminal"]] as Array<[SidebarView, string]>) : []),
    [{ kind: "activity" }, "Activity"],
    [{ kind: "projects" }, "Projects"],
    [{ kind: "changelog" }, "Changelog"],
    [{ kind: "settings" }, "Settings"],
  ];

  return (
    <>
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        side={side}
        align={align}
        className="w-64"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DropdownMenuLabel>Open windows · {open.length}</DropdownMenuLabel>
        {open.map((window, index) => (
          <Fragment key={window.id}>
            <DropdownMenuItem
              onSelect={() => apply((current) => reveal(current, window.id))}
              className={cn("group/item", window.id === ws.focusedId && "bg-accent/50 font-medium")}
            >
              {viewIcon(window.view)}
              <span className="min-w-0 flex-1 truncate">{titleOf(window.view)}</span>
              {window.tabs ? <span className="text-[10px] font-normal tabular-nums text-muted-foreground">{window.tabs.length} tabs</span> : null}
              {window.minimized ? <span className="text-[10px] font-normal text-muted-foreground">minimised</span> : null}
              <span className="text-[10.5px] tabular-nums text-muted-foreground">Alt+{index + 1}</span>
              <InlineAction
                label={window.tabs ? `Close the window and its ${window.tabs.length} tabs` : `Close ${titleOf(window.view)}`}
                onClick={() => apply((current) => closeWindow(current, window.id, Date.now()))}
              >
                <X />
              </InlineAction>
            </DropdownMenuItem>
            {/* A tab group's tabs, under their window: pick one to show it, × closes only it. */}
            {window.tabs?.map((tab, tabIndex) => {
              const shown = viewKey(tab) === viewKey(window.view);
              return (
                <DropdownMenuItem
                  key={viewKey(tab)}
                  onSelect={() => apply((current) => activateTab(current, window.id, tabIndex))}
                  className={cn("group/item pl-7 text-[12.5px]", shown ? "text-foreground" : "text-muted-foreground")}
                >
                  {viewIcon(tab)}
                  <span className="min-w-0 flex-1 truncate">{titleOf(tab)}</span>
                  {shown ? <span className="text-[10px] text-muted-foreground">showing</span> : null}
                  <InlineAction label={`Close the ${titleOf(tab)} tab`} onClick={() => apply((current) => closeTab(current, window.id, tabIndex, Date.now()))}>
                    <X />
                  </InlineAction>
                </DropdownMenuItem>
              );
            })}
          </Fragment>
        ))}
        {open.some((window) => window.minimized) ? (
          <DropdownMenuItem onSelect={() => apply(restoreAll)}>
            <ChevronsUpDown /> Restore all
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={ws.shelf.length === 0}>
            <ArchiveRestore /> Closed
            <span className="ml-auto text-[10.5px] tabular-nums text-muted-foreground">{ws.shelf.length || ""}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-64">
            <DropdownMenuLabel>Restore where it was · or here, beside</DropdownMenuLabel>
            {ws.shelf.map((entry) => (
              <DropdownMenuItem key={entry.id} onSelect={() => apply((current) => restoreWindow(current, entry.id))}>
                {viewIcon(entry.view)}
                <span className="min-w-0 flex-1 truncate">{titleOf(entry.view)}</span>
                {entry.tabOf ? <span className="text-[10px] text-muted-foreground">tab</span> : null}
                <InlineAction label={`Restore ${titleOf(entry.view)} here`} onClick={() => apply((current) => restoreWindow(current, entry.id, { kind: "replace" }))}>
                  <AppWindow />
                </InlineAction>
                <InlineAction
                  label={`Restore ${titleOf(entry.view)} beside`}
                  onClick={() =>
                    apply((current) => restoreWindow(current, entry.id, { kind: "split", targetId: current.focusedId, zone: "right" }))
                  }
                >
                  <Columns2 />
                </InlineAction>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => apply(clearShelf)}>
              <Trash2 /> Clear
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Plus /> New window
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-60">
            <DropdownMenuItem onSelect={() => apply(openEmptyWindow)}>
              <AppWindow /> New empty window
              <span className="ml-auto text-[10.5px] text-muted-foreground">Alt+N</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={boards.length === 0}>
                <SquareKanban /> Board
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[60dvh] w-60 overflow-y-auto scrollbar-slim">
                {boards.map((detail) => (
                  <DropdownMenuItem key={detail.board.id} onSelect={() => beside({ kind: "board", boardId: detail.board.id })}>
                    <span className="truncate">{detail.board.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <BotMessageSquare /> Agent
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[60dvh] w-60 overflow-y-auto scrollbar-slim">
                <DropdownMenuItem onSelect={() => beside({ kind: "agents" })}>All agents</DropdownMenuItem>
                <DropdownMenuSeparator />
                {agents.map((agent) => (
                  <DropdownMenuItem key={agent.id} onSelect={() => beside({ kind: "agents", agentId: agent.id })}>
                    <span className="truncate">{agent.name}</span>
                    <span className="ml-auto truncate text-[10.5px] text-muted-foreground">@{agent.handle}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            {views.map(([view, label]) => (
              <DropdownMenuItem key={view.kind} onSelect={() => beside(view)}>
                {viewIcon(view)} {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub open={layoutsSub} onOpenChange={setLayoutsSub}>
          <DropdownMenuSubTrigger>
            <LayoutTemplate /> Layouts
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-[70dvh] w-64 overflow-y-auto scrollbar-slim">
            {layouts.length > 0 && <DropdownMenuLabel>Saved · {layouts.length}</DropdownMenuLabel>}
            {layouts.map((layout) => (
              <DropdownMenuItem
                key={layout.id}
                onSelect={() => applySaved(layout)}
                className={cn("group/item", layout.id === active?.id && "bg-accent/50 font-medium")}
              >
                {layout.id === active?.id ? <Check /> : <LayoutTemplate />}
                <span className="min-w-0 flex-1 truncate">{layout.name}</span>
                <span className="text-[10.5px] tabular-nums text-muted-foreground">{countWindows(layout.layout)}</span>
                {canSave && (
                  <>
                    <InlineAction label={`Rename ${layout.name}`} onClick={() => askName({ mode: "rename", layout })}
                    >
                      <Pencil />
                    </InlineAction>
                    <InlineAction
                      label={`Update ${layout.name} to these windows`}
                      onClick={() => void overwrite(layout).catch(() => undefined)}
                    >
                      <Save />
                    </InlineAction>
                    <InlineAction label={`Delete ${layout.name}`} onClick={() => void layoutActions.run({ kind: "delete", layoutId: layout.id }).catch(() => undefined)}>
                      <Trash2 />
                    </InlineAction>
                  </>
                )}
              </DropdownMenuItem>
            ))}
            {canSave && (
              <>
                {layouts.length > 0 && <DropdownMenuSeparator />}
                {/* Save writes back to the layout these windows came from; with none it asks for a name, as Save as does. */}
                <DropdownMenuItem
                  disabled={layoutActions.pending}
                  onSelect={() => (active ? void overwrite(active).catch(() => undefined) : askName({ mode: "save" }))}
                >
                  <Save />
                  <span className="min-w-0 flex-1 truncate">{active ? `Save “${active.name}”` : "Save…"}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => askName({ mode: "save" })}>
                  <CopyPlus /> Save as…
                </DropdownMenuItem>
              </>
            )}
            {(layouts.length > 0 || canSave) && <DropdownMenuSeparator />}
            <DropdownMenuLabel>Presets</DropdownMenuLabel>
            <DropdownMenuItem disabled={!firstBoard} onSelect={() => firstBoard && apply((current) => setActiveLayout(presetSideBySide(current, firstBoard, firstAgent), null))}>
              <Columns2 /> Board + agent
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={boards.length < 2}
              onSelect={() =>
                apply((current) =>
                  setActiveLayout(
                    presetSideBySide(current, { kind: "board", boardId: boards[0]!.board.id }, { kind: "board", boardId: boards[1]!.board.id }),
                    null,
                  ),
                )
              }
            >
              <Columns2 /> Two boards
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => apply((current) => setActiveLayout(resetLayout(current), null))}>
              <RotateCcw /> Reset layout
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <PanelLeft /> Navigation
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-52">
            {DOCKS.map(([dock, label, icon]) => (
              <DropdownMenuItem
                key={dock}
                onSelect={() => apply((current) => setNav(current, { dock, hidden: false }))}
                className={cn(ws.nav.dock === dock && "bg-accent/50 font-medium")}
              >
                {icon} Dock {label.toLowerCase()}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => apply((current) => setNav(current, { hidden: !current.nav.hidden }))}>
              {ws.nav.hidden ? <Eye /> : <EyeOff />} {ws.nav.hidden ? "Show navigation" : "Hide navigation"}
              <span className="ml-auto text-[10.5px] text-muted-foreground">[</span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Keyboard shortcuts</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-64">
            {WINDOW_SHORTCUTS.map(([keys, label]) => (
              <div key={keys} className="flex items-center gap-3 px-2 py-1 text-[12.5px]">
                <span className="flex-1">{label}</span>
                <kbd className="rounded border bg-muted px-1.5 text-[10.5px] text-muted-foreground">{keys}</kbd>
              </div>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
    {nameDialog}
    </>
  );
}

/**
 * Saved layouts as commands, shared by the Windows menu's Layouts submenu and the
 * navigation's Layouts menu: apply, overwrite, and the name dialog for Save as and
 * Rename. `closeMenu` closes the menu asking for a name; the dialog opens from the
 * menu's `onCloseAutoFocus`, once it has gone.
 */
function useLayoutCommands({
  ws,
  apply,
  allBoards,
  agents,
  closeMenu,
}: {
  ws: Workspace;
  apply: (op: (ws: Workspace) => Workspace) => void;
  allBoards: BoardDetail[];
  agents: Array<{ id: string }>;
  closeMenu: () => void;
}) {
  const layouts = useLayouts();
  const layoutActions = useLayoutActions();
  const canSave = useCan("member");
  /** The name dialog: saving the current windows under a new name, or renaming a saved layout. */
  const [naming, setNaming] = useState<Naming>(null);
  /**
   * A name dialog asked for from inside the menu opens only once the menu has closed:
   * opened at once, the menu unmounting afterwards dropped focus to the page and the
   * name box could not be typed in.
   */
  const pendingNaming = useRef<Naming>(null);
  const askName = (next: Exclude<Naming, null>) => {
    pendingNaming.current = next;
    closeMenu();
  };
  const onCloseAutoFocus = (event: Event) => {
    const next = pendingNaming.current;
    if (!next) return;
    pendingNaming.current = null;
    event.preventDefault(); // the dialog takes focus, not the trigger
    setNaming(next);
  };
  const exists = (view: SidebarView) => layoutViewExists(view, allBoards, agents);
  const applySaved = (layout: WorkspaceLayout) => apply((current) => applyLayout(current, layout, exists));
  /** The saved layout these windows came from, if it still exists: what Save overwrites. */
  const active = ws.layoutId ? layouts.find((layout) => layout.id === ws.layoutId) ?? null : null;
  const markActive = (layoutId: string | null) => apply((current) => setActiveLayout(current, layoutId));
  const currentTree = () => {
    const { layout, nav } = layoutFromWorkspace(ws);
    // The web's tree is the server's shape; only `archive` differs, and layoutFromWorkspace writes it as Settings.
    return { layout: layout as SavedLayoutNode, nav };
  };
  /** Overwrites a saved layout with these windows, and makes it the one Save writes to. */
  const overwrite = async (layout: WorkspaceLayout) => {
    const { layout: tree, nav } = currentTree();
    // A layout that leaves the navigation alone keeps doing so.
    await layoutActions.run({ kind: "replace", layoutId: layout.id, name: layout.name, layout: tree, nav: layout.nav ? nav : null });
    markActive(layout.id);
  };
  const nameDialog = (
    <LayoutNameDialog
      naming={naming}
      windowCount={windows(ws).length}
      layouts={layouts}
      pending={layoutActions.pending}
      onClose={() => setNaming(null)}
      onSubmit={async (name) => {
        if (naming?.mode === "rename") {
          if (name !== naming.layout.name) await layoutActions.run({ kind: "rename", layoutId: naming.layout.id, name });
        } else {
          // Save as onto a name already taken replaces that layout (the dialog says so) rather than being refused.
          const taken = sameName(layouts, name);
          if (taken) await overwrite(taken);
          else {
            const created = await layoutActions.run({ kind: "save", name, ...currentTree() });
            markActive(created.id);
          }
        }
        setNaming(null);
      }}
    />
  );
  return { layouts, layoutActions, canSave, active, applySaved, overwrite, askName, onCloseAutoFocus, nameDialog };
}

/**
 * The navigation's Layouts entry (desktop): one row per saved layout, the active one
 * checked and the one Alt+L goes to next marked, then Save / Save as… and Manage…,
 * which opens the Windows menu at its Layouts submenu. Picking a row is the Windows
 * menu's apply.
 */
export function LayoutsMenu({
  children,
  side = "right",
  align = "start",
  hint,
  ...props
}: Omit<Parameters<typeof WindowsMenu>[0], "open" | "onOpenChange" | "openLayouts"> & {
  /** A tooltip on the trigger (the rail and the strip, which show no label). */
  hint?: string;
}) {
  const { ws, apply, allBoards = props.boards, agents } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  /** Manage… opens the Windows menu once this one has closed, as a name dialog does. */
  const pendingManage = useRef(false);
  const { layouts, layoutActions, canSave, active, applySaved, overwrite, askName, onCloseAutoFocus, nameDialog } = useLayoutCommands({
    ws,
    apply,
    allBoards,
    agents,
    closeMenu: () => setMenuOpen(false),
  });
  const nextId = cycleLayoutId(
    layouts.map((layout) => layout.id),
    active?.id ?? null,
    1,
  );
  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        {hint ? (
          <Hint label={hint} side={side}>
            <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
          </Hint>
        ) : (
          <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          side={side}
          align={align}
          className="max-h-[70dvh] w-64 overflow-y-auto scrollbar-slim"
          onCloseAutoFocus={(event) => {
            if (pendingManage.current) {
              pendingManage.current = false;
              event.preventDefault();
              setManageOpen(true);
              return;
            }
            onCloseAutoFocus(event);
          }}
        >
          <DropdownMenuLabel>Layouts{layouts.length > 0 ? ` · ${layouts.length}` : ""}</DropdownMenuLabel>
          {layouts.length === 0 && (
            <p className="px-2 pb-1.5 text-[12px] text-muted-foreground">
              None saved yet. Arrange the windows, then Save to switch back to them here or with Alt+L.
            </p>
          )}
          {layouts.map((layout) => (
            <DropdownMenuItem
              key={layout.id}
              onSelect={() => applySaved(layout)}
              className={cn(layout.id === active?.id && "bg-accent/50 font-medium")}
            >
              {layout.id === active?.id ? <Check /> : <LayoutTemplate />}
              <span className="min-w-0 flex-1 truncate">{layout.name}</span>
              {layout.id === nextId && <span className="text-[10.5px] text-muted-foreground">Alt+L</span>}
              <span className="w-4 text-right text-[10.5px] tabular-nums text-muted-foreground">{countWindows(layout.layout)}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {canSave && (
            <>
              <DropdownMenuItem
                disabled={layoutActions.pending}
                onSelect={() => (active ? void overwrite(active).catch(() => undefined) : askName({ mode: "save" }))}
              >
                <Save />
                <span className="min-w-0 flex-1 truncate">{active ? `Save “${active.name}”` : "Save…"}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => askName({ mode: "save" })}>
                <CopyPlus /> Save as…
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem
            onSelect={() => {
              pendingManage.current = true;
            }}
          >
            <Settings2 /> Manage…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {nameDialog}
      {/* Mounted only while open, so its empty anchor adds no gap to the navigation. */}
      {manageOpen && (
        <WindowsMenu {...props} side={side} align={align} open onOpenChange={setManageOpen} openLayouts>
          <span aria-hidden className="pointer-events-none size-0" />
        </WindowsMenu>
      )}
    </>
  );
}

type Naming = { mode: "save" } | { mode: "rename"; layout: WorkspaceLayout } | null;

const sameName = (layouts: WorkspaceLayout[], name: string) =>
  layouts.find((layout) => layout.name.trim().toLowerCase() === name.trim().toLowerCase()) ?? null;

const countWindows = (node: SavedLayoutNode): number =>
  node.type === "window" ? 1 : node.children.reduce((sum, child) => sum + countWindows(child), 0);

/**
 * Names a layout, for Save as and Rename. Saving under a name already taken replaces
 * that layout, and the dialog says so before it does; a rename onto one is refused by
 * the server and keeps the dialog open, the toast saying why.
 */
function LayoutNameDialog({
  naming,
  windowCount,
  layouts,
  pending,
  onClose,
  onSubmit,
}: {
  naming: Naming;
  windowCount: number;
  layouts: WorkspaceLayout[];
  pending: boolean;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [shownFor, setShownFor] = useState<Naming>(null);
  // Fill the box each time it opens: the layout's own name to rename, empty to save.
  if (naming !== shownFor) {
    setShownFor(naming);
    if (naming) setName(naming.mode === "rename" ? naming.layout.name : "");
  }
  const renaming = (naming ?? shownFor)?.mode === "rename";
  const replacing = !renaming && name.trim() ? sameName(layouts, name) : null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed) void onSubmit(trimmed).catch(() => undefined);
  };
  return (
    <Dialog open={naming !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{renaming ? "Rename layout" : "Save layout as"}</DialogTitle>
            <DialogDescription>
              {renaming
                ? "Its windows stay as they are."
                : `${windowCount === 1 ? "This window" : `These ${windowCount} windows`}, their sizes and where the navigation sits, under a name in Windows → Layouts. Saved layouts are shared with everyone who signs in.`}
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={name}
            maxLength={60}
            placeholder="e.g. Sprint + Frontend"
            aria-label="Layout name"
            onChange={(event) => setName(event.target.value)}
          />
          {replacing && (
            <p className="-mt-2 text-[12.5px] text-muted-foreground">
              “{replacing.name}” already exists. Saving replaces its windows with these.
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || pending}>
              {renaming ? "Rename" : replacing ? "Replace" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** One window again, showing what the focused one did; the rest go to Closed. */
function resetLayout(ws: Workspace): Workspace {
  const keep = focusedWindow(ws);
  let next = ws;
  for (const window of windows(ws)) if (window.id !== keep.id) next = closeWindow(next, window.id, Date.now());
  return unmaximize(next);
}

/** A small button inside a menu row: acts without selecting the row (or closing the menu). */
function InlineAction({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="grid size-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground [&_svg]:size-3.5"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}
